import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import WarningIcon from "@material-ui/icons/Warning";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { appUrl } from "../../../../../../utils/config";
//import ReviewConsumptionDialog from "./ReviewConsumptionDialog";
import CustomDatePickerMui from "../../models/inputs/DateInput";

const useStyles = makeStyles(() => ({
  cancelButton: {
    fontSize: "1rem",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    color: "#FF0000",
  },
  dialogTitle: {
    textAlign: "center",
    fontWeight: 600,
    fontSize: "1.6rem",
    marginTop: "8px",
    marginBottom: "20px",
  },
  warningIconLarge: {
    fontSize: "4rem",
    color: "#D32F2F",
    display: "block",
    margin: "0 auto 8px",
  },
  infoHeaderRow: {
    backgroundColor: "#EBF3FB",
    border: "1px solid #c5ddf0",
    borderRadius: "6px",
    padding: "12px 20px",
    marginBottom: "20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  infoLabel: {
    fontWeight: 700,
    fontSize: "0.82rem",
    color: "#555",
    marginBottom: "2px",
  },
  infoValue: {
    fontSize: "0.9rem",
    fontWeight: 500,
  },
  sectionHeader: {
    fontWeight: 700,
    fontSize: "0.95rem",
    marginTop: "20px",
    marginBottom: "8px",
  },
  tableContainer: {
    boxShadow: "none",
    border: "1px solid #d0d0d0",
    borderRadius: "6px",
    marginBottom: "8px",
    overflowX: "hidden",
  },
  tableHeaderCell: {
    fontWeight: 700,
    backgroundColor: "#F5F5F5",
    fontSize: "0.72rem",
    padding: "8px 8px",
    whiteSpace: "normal",
    lineHeight: 1.3,
  },
  tableCell: {
    fontSize: "0.78rem",
    padding: "8px 8px",
  },
  warningText: {
    color: "#000",
    fontSize: "0.85rem",
    marginBottom: "8px",
  },
  warningInline: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    color: "#D32F2F",
    fontWeight: 600,
  },
  warningIconSmall: {
    fontSize: "1rem",
    color: "#D32F2F",
  },
  yesWarning: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    color: "#D32F2F",
    fontWeight: 700,
  },
  rejectButton: {
    backgroundColor: "#1565C0",
    color: "#fff",
    borderRadius: "20px",
    textTransform: "none",
    padding: "8px 24px",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: "#0d47a1",
    },
  },
  approveButton: {
    backgroundColor: "#EC6535",
    color: "#fff",
    borderRadius: "20px",
    textTransform: "none",
    padding: "8px 28px",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: "#d4572e",
    },
  },
  errorText: {
    color: "#D32F2F",
    fontSize: "0.82rem",
    marginTop: "8px",
    textAlign: "center",
  },
  successDialog: {
    textAlign: "center",
    padding: "40px 20px",
  },
  successIcon: {
    fontSize: "80px",
    color: "#39811D",
    marginBottom: "20px",
  },
  successTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#333",
    marginBottom: "16px",
  },
  okButton: {
    backgroundColor: "#EC6535",
    color: "#fff",
    borderRadius: "20px",
    textTransform: "none",
    padding: "8px 24px",
    marginTop: "20px",
    "&:hover": {
      backgroundColor: "#d4572e",
    },
  },
}));

// Format Date object → "YYYY-MM-DD" for <input type="date">
const toInputDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Round a numeric value to 3 decimal places for display
const toDecimal = (val) => {
  if (val === null || val === undefined || val === "-") return val;
  const num = Number(val);
  if (isNaN(num)) return val;
  return parseFloat(num.toFixed(3));
};

// Format any date string → display string
const formatDisplayDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).replace(/ /g, "-");
};

// Build consumed batch rows from fetchComponentAllocationPlannedAndActualNew response
const buildConsumedRows = (allocationData) => {
  if (!Array.isArray(allocationData)) return [];
  return allocationData.map((item) => {
    const planned = item.plannedLotNumberAllocationDetails || [];
    const actual = item.actualConsumptionDetails || [];

    // Build lot-level rows merging planned and actual
    const lotMap = {};
    planned.forEach((p) => {
      const key = p.lotNumber || "unknown";
      lotMap[key] = {
        batchNumber: p.lotNumber || "",
        plannedAllocatedQty: p.plannedAllocatedQuantity || 0,
        actualConsumedQty: 0,
      };
    });
    actual.forEach((a) => {
      const key = a.componentBatchNumber || "unknown";
      if (lotMap[key]) {
        lotMap[key].actualConsumedQty = a.BomComponentConsumedQuantity || 0;
      } else {
        lotMap[key] = {
          batchNumber: a.componentBatchNumber || "",
          plannedAllocatedQty: 0,
          actualConsumedQty: a.BomComponentConsumedQuantity || 0,
        };
      }
    });

    const lots = Object.values(lotMap);

    return {
      materialCode: item.componentCode || "",
      materialName: item.componentName || "",
      bomQuantity: item.componentQuantity != null ? item.componentQuantity : "",
      uom: item.bomComponentRefUOM || item.uom || "",
      lots,
      consumedQtyVariance: item.quantityVarianceValue,
      varianceWarning: item.quantityVariancePresent || false,
      batchesMismatch: item.lotsMismatch || false,
    };
  });
};

const VarianceMismatchDialog = ({ open, handleClose, row, onRefreshData }) => {
  const classes = useStyles();

  // Allocation data state
  const [allocationData, setAllocationData] = useState([]);
  const [consumedRows, setConsumedRows] = useState([]);
  const [loadingAllocation, setLoadingAllocation] = useState(false);
  const [qtyVarianceAcceptanceLimit, setQtyVarianceAcceptanceLimit] = useState(null);

  // Snapshot of planned allocated quantities at the time this dialog opened.
  // Keyed as "componentCode__lotNumber" → plannedAllocatedQuantity.
  // Passed to ReviewConsumptionDialog so it can display "Previous Planned Allocated Qty"
  // independently of its own fresh API call.
  const [plannedQtySnapshot, setPlannedQtySnapshot] = useState({});

  // Stat date state
  const presentDate = new Date();

  // ── Section 1: Stat and Delivery Date Variance ──
  // Determine direction: early → stat date is AFTER present, later → stat date is BEFORE present
  const statDate = row?.statisticalDate ? new Date(row.statisticalDate) : null;
  const isEarlyDateVariance = statDate ? statDate.getTime() > presentDate.getTime() : false;
  // Section shows only when variance is present AND the applicable direction's updateType requires action
  // (Manual or Automatic after approval — "Automatic without approval" hides the section)
  const showStatDateSection =
    row?.statDateVariancePresent === true &&
    (
      (isEarlyDateVariance  && (row?.earlyDateUpdateType === "Manual" || row?.earlyDateUpdateType === "Automatic after approval")) ||
      (!isEarlyDateVariance && (row?.laterDateUpdateType === "Manual" || row?.laterDateUpdateType === "Automatic after approval"))
    );
  // Apply update type based on direction (BCM-744 p.6 example):
  //   If early → use earlyDateUpdateType; if later → use laterDateUpdateType
  //   "Manual" → calendar editable; "Automatic after approval" → calendar read-only
  const isStatDateEditable =
    (isEarlyDateVariance  && row?.earlyDateUpdateType === "Manual") ||
    (!isEarlyDateVariance && row?.laterDateUpdateType === "Manual");
  // Column always shown when section is visible; editability controlled separately inside
  const showUpdateStatDateCol = showStatDateSection;

  // ── Section 2: Consumed Batch Variance/Mismatch ──
  const consumedQtyLimitPct = row?.consumedQtyVarianceLimitPercentage ?? null;
  // For each component: varianceUnits = |bomQty - totalActual|, limitUnits = bomQty × (limit% / 100)
  // Both + and - variance handled by Math.abs() using the same single limit
  const isConsumedQtyOutsideLimit = consumedQtyLimitPct !== null && consumedRows.some((item) => {
    const bomQty      = item.bomQuantity || 0;
    const totalActual = (item.lots || []).reduce((sum, lot) => sum + (lot.actualConsumedQty || 0), 0);
    const varianceUnits = Math.abs(bomQty - totalActual);
    const limitUnits    = bomQty * (consumedQtyLimitPct / 100);
    return varianceUnits > limitUnits;
  });
  const hasAnyLotMismatch = consumedRows.some((item) => item.batchesMismatch === true);
  const showConsumedQtyVarianceCol = row?.consumedQtyVarianceApprovalRequired === true && isConsumedQtyOutsideLimit;
  const showBatchesMismatchCol     = row?.lotMismatchApprovalRequired === true && hasAnyLotMismatch;
  const showConsumedBatchSection   = showConsumedQtyVarianceCol || showBatchesMismatchCol;

  // ── Section 3: Produced Qty Variance ──
  // Determine direction: more → producedQty > targetQty; less → producedQty ≤ targetQty
  const isMoreQtyVariance = (row?.producedQuantity ?? 0) > (row?.targetQuantity ?? 0);
  // Section shows only when variance is present AND the applicable direction's updateType requires action
  // (Manual or Automatic after approval — "Automatic without approval" hides the section)
  const showProductQtySection =
    row?.producedQtyVariancePresent === true &&
    (
      (!isMoreQtyVariance && (row?.lessProducedQtyUpdateType === "Manual" || row?.lessProducedQtyUpdateType === "Automatic after approval")) ||
      (isMoreQtyVariance  && (row?.moreProducedQtyUpdateType === "Manual" || row?.moreProducedQtyUpdateType === "Automatic after approval"))
    );
  // Design slide 22: both limit columns always show once the section is visible,
  // regardless of direction — only "Update SAP PO Qty to" (showUpdatePOQtyCol,
  // isProductQtyEditable below) varies by direction + BCM-744 config. Previously
  // only the applicable direction's column showed; kept as named flags (rather
  // than inlining `true` at every call site) since they also feed the "No data
  // available" colSpan math below.
  const showLessVarianceLimitCol = true;
  const showMoreVarianceLimitCol = true;
  const isProductQtyEditable =
    (!isMoreQtyVariance && row?.lessProducedQtyUpdateType === "Manual") ||
    (isMoreQtyVariance  && row?.moreProducedQtyUpdateType === "Manual");
  // Column always shown when section is visible; editability controlled separately inside
  const showUpdatePOQtyCol = showProductQtySection;

  const [updateStatDate, setUpdateStatDate] = useState("");

  // Update SAP PO Qty input (Section 3)
  const [updatePOQty, setUpdatePOQty] = useState("");

  // Approve loading/error/success state
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState("");
  const [approveSuccess, setApproveSuccess] = useState(false);

  // Review Consumption dialog state
 // const [reviewConsumptionOpen, setReviewConsumptionOpen] = useState(false);
  const [approvedPOQty, setApprovedPOQty] = useState(null);
  const [cmoProductCode, setCmoProductCode] = useState(null);
  const [cmoProductDescription, setCmoProductDescription] = useState(null);

  // Fetch allocation data when dialog opens
  useEffect(() => {
    if (!open) return;

    // Reset state
    setApproveError("");
    setApproveSuccess(false);
    setQtyVarianceAcceptanceLimit(null);
    // Auto-populate stat date with present date
    setUpdateStatDate(new Date());
    // Auto-populate PO qty with produced quantity
    setUpdatePOQty(row?.producedQuantity ?? "");

    const batchID = row?.schrockenDeliveryScheduleBatchID;
    const batchSize = row?.batchSize;

    if (!batchID || !batchSize) return;

    setLoadingAllocation(true);
    const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchComponentAllocationPlannedAndActualNewDirect/${batchID}/${batchSize}`;

    axios
      .get(url)
      .then((res) => {
        if (res.data.status) {
          const data = res.data.data || [];
          setAllocationData(data);
          setConsumedRows(buildConsumedRows(data));
          setQtyVarianceAcceptanceLimit(res.data.qtyVarianceAcceptanceLimit ?? null);
          setCmoProductCode(res.data.cmoProductCode || null);
          setCmoProductDescription(res.data.cmoProductDescription || null);

          // Build and store planned qty snapshot for ReviewConsumptionDialog memory
          const snapshot = {};
          data.forEach((item) => {
            const code = item.componentCode || "";
            (item.plannedLotNumberAllocationDetails || []).forEach((p) => {
              const key = `${code}__${p.lotNumber}`;
              snapshot[key] = p.plannedAllocatedQuantity || 0;
            });
          });
          setPlannedQtySnapshot(snapshot);
        } else {
          setAllocationData([]);
          setConsumedRows([]);
          setPlannedQtySnapshot({});
        }
      })
      .catch((err) => {
        console.error("Error fetching allocation data:", err);
        setAllocationData([]);
        setConsumedRows([]);
      })
      .finally(() => setLoadingAllocation(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleReject = () => {
    handleClose();
    if (onRefreshData) onRefreshData();
  };

  const handleApprove = async () => {
    if (isProductQtyEditable) {
      const parsedQty = Number(String(updatePOQty ?? "").trim());
      if (!Number.isFinite(parsedQty) || parsedQty <= 0) {
        setApproveError("Please enter a valid positive number for Update SAP PO Qty before approving.");
        return;
      }
    }
    setApproving(true);
    setApproveError("");

    const statusID = 39;
    const statusDescription = "Specs Approved";
    const statusDisplayName = "Specs Approved";

    const firstComponent = allocationData.length > 0 ? allocationData[0] : null;
    const quantityVarianceValue = firstComponent?.quantityVarianceValue ?? 0;
    const lotMismatch = firstComponent?.lotsMismatch ?? false;
    const quantityVariancePresent = firstComponent?.quantityVariancePresent ?? false;

    const payload = {
      sponsorName: row?.sponsorName,
      BMSproductCode: row?.fgMaterialCode,
      BMSproductDesc: row?.fgMaterialName,
      CMOproductCode: cmoProductCode || row?.cmoProductCode,
      CMOproductDesc: cmoProductDescription || row?.cmoProductDescription,
      scheduleAgreementNumber: row?.scheduleAgreementNumber,
      vendorBatchNumber: row?.vendorBatchNumber,
      vendorMosymphonyID: row?.vendorMosymphonyID,
      cmoBatchNumber: row?.cmoBatchNumber,
      brandOwnerMosymphonyID: row?.brandOwnerMosymphonyID,
      schrockenSLID: row?.schrockenSLID,
      QtyVariance: quantityVarianceValue,
      //LotMismatch: lotMismatch,
      lotMismatch: lotMismatch,
      quantityVariancePresent: quantityVariancePresent,
      producedQuantityVariancePresent: row?.producedQtyVariancePresent ?? false,
      producedQtyMoreThanUpperLimit: row?.producedQtyMoreThanUpperLimit ?? false,
      statDateVariancePresent: row?.statDateVariancePresent ?? false,
      earlyPODateUpdateInERP: row?.earlyPODateUpdateInERP ?? false,
      earlyDateUpdateType: row?.earlyDateUpdateType ?? null,
      laterPODateUpdateInERP: row?.laterPODateUpdateInERP ?? false,
      laterDateUpdateType: row?.laterDateUpdateType ?? null,
      lessProducedQtyUpdateInERP: row?.lessProducedQtyUpdateInERP ?? false,
      lessProducedQtyUpdateType: row?.lessProducedQtyUpdateType ?? null,
      moreProducedQtyUpdateInERP: row?.moreProducedQtyUpdateInERP ?? false,
      moreProducedQtyUpdateType: row?.moreProducedQtyUpdateType ?? null,
      batchDetailsList: (() => {
        const entry = {
          updateFromProcessFlow: true,
          updateStatusFromProcessFlow: true,
          statusID: statusID,
          statusDescription: statusDescription,
          SASLBatchStatusID: statusID,
          statusDisplayName: statusDisplayName,
          schrockenDeliveryScheduleBatchID: row?.schrockenDeliveryScheduleBatchID,
        };
        // Scenario A: Produced qty variance — send producedQuantity based on direction + updateType
        if (showProductQtySection) {
          if (isProductQtyEditable) {
            // Manual → planner's edited value
            const qty = Number(String(updatePOQty ?? "").trim());
            if (Number.isFinite(qty) && qty > 0) {
              entry.producedQuantity = qty;
              entry.batchSize = qty;
            }
          } else if (
            (!isMoreQtyVariance && row?.lessProducedQtyUpdateType === "Automatic after approval") ||
            (isMoreQtyVariance  && row?.moreProducedQtyUpdateType === "Automatic after approval")
          ) {
            // Automatic after approval → echo from dashboard (no textbox shown in UI)
            entry.producedQuantity = row?.producedQuantity ?? null;
            entry.batchSize = row?.producedQuantity ?? null;
          }
        }
        // Scenario B: Stat date variance — send date fields based on direction + updateType
        if (showStatDateSection) {
          if (isStatDateEditable && updateStatDate) {
            // Manual → planner's picked date
            const parsedDate = new Date(updateStatDate);
            if (!isNaN(parsedDate.getTime())) {
              const isoDate = parsedDate.toISOString();
              entry.statisticalDate = isoDate;
              entry.deliveryDate = isoDate;
              entry.committedDeliveryDate = isoDate;
            }
          } else if (isEarlyDateVariance && row?.earlyDateUpdateType === "Automatic after approval") {
            // Early + Automatic after approval → send the PRESENT DATE, matching
            // what's shown in the UI (defect: this used to echo the stale
            // statisticalDate/deliveryDate from SAP instead).
            const isoDate = presentDate.toISOString();
            entry.statisticalDate = isoDate;
            entry.deliveryDate = isoDate;
            entry.committedDeliveryDate = isoDate;
          } else if (!isEarlyDateVariance && row?.laterDateUpdateType === "Automatic after approval") {
            // Later + Automatic after approval → echo from dashboard, unchanged.
            entry.statisticalDate = row?.statisticalDate ?? null;
            entry.deliveryDate = row?.deliveryDate ?? null;
            entry.committedDeliveryDate = row?.committedDeliveryDate ?? null;
          }
        }
        return [entry];
      })(),
    };

    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateDeliveryScheduleBatch`;
      const res = await axios.post(url, payload);
      if (res.data.status) {
        setApprovedPOQty(row?.targetQuantity);
        setApproveSuccess(true);
      } else {
        setApproveError(res.data.message || "Approval failed. Please try again.");
      }
    } catch (err) {
      console.error("Error approving variance:", err);
      setApproveError(err.response?.data?.message || "An error occurred. Please try again.");
    } finally {
      setApproving(false);
    }

    // Commented out — replaced by updateDeliveryScheduleBatch above
    // const { statDateVariancePresent, producedQtyVariancePresent } = row || {};
    // const approvedStatusID = 39;
    // const approvedStatusDescription = "Specs Approved";
    // const oldPayload = {
    //   BMSproductCode: row?.fgMaterialCode,
    //   BMSproductDesc: row?.fgMaterialName,
    //   scheduleAgreementNumber: row?.scheduleAgreementNumber,
    //   vendorBatchNumber: row?.vendorBatchNumber,
    //   vendorMosymphonyID: row?.vendorMosymphonyID,
    //   brandOwnerMosymphonyID: row?.brandOwnerMosymphonyID,
    //   batchDetailsList: [{ statusID: approvedStatusID, statusDescription: approvedStatusDescription, schrockenDeliveryScheduleBatchID: row?.schrockenDeliveryScheduleBatchID }],
    //   statDateVariancePresent: !!statDateVariancePresent,
    //   producedQtyVariancePresent: !!producedQtyVariancePresent,
    //   scheduleLineDetails: { committedDeliveryDate: row?.committedDeliveryDate, schrockenSLID: row?.schrockenSLID, statisticalDate: row?.statisticalDate, targetQuantity: row?.targetQuantity, deliveryDate: row?.deliveryDate, committedQuantity: row?.committedQuantity === null ? 0 : row?.committedQuantity },
    // };
    // const approveUrl = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/approveVarianceMismatch`;
    // const approveRes = await axios.post(approveUrl, oldPayload);
  };

  // Called when user clicks OK on success screen
  const handleOk = () => {
    const { producedQtyVariancePresent } = row || {};
    // if (producedQtyVariancePresent && row?.consumptionSameAsAllocation) {
    //   setApproveSuccess(false);
    //   setReviewConsumptionOpen(true);
    // } else {
      setApproveSuccess(false);
      handleClose();
      if (onRefreshData) onRefreshData();
    
  };

  // Product Qty Variance row
  const productQtyRow = row
    ? {
        productCode: row.fgMaterialCode || "",
        productName: row.fgMaterialName || "",
        uom: row.batchUOM || "",
        poQty: row.targetQuantity || 0,
        producedQty: row.producedQuantity || 0,
        variance:
          row.targetQuantity > 0
            ? Math.abs(
                (((row.producedQuantity || 0) - row.targetQuantity) /
                  row.targetQuantity) *
                  100
              ).toFixed(3)
            : "0.000",
      }
    : null;

  return (
    <>
      <Dialog
        open={open && !approveSuccess}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{ style: { overflowX: "hidden" } }}
      >
        <DialogContent style={{ padding: "16px 20px 8px 20px", overflowX: "hidden" }}>
          <>
          {/* Cancel */}
          <Grid container justifyContent="flex-end" style={{ marginBottom: "8px" }}>
            <Typography
              variant="body2"
              className={classes.cancelButton}
              onClick={handleClose}
            >
              Cancel&nbsp;
              <CloseIcon fontSize="large" />
            </Typography>
          </Grid>

          {/* Icon + Title */}
          <Grid container justifyContent="center" direction="column" alignItems="center">
            <WarningIcon className={classes.warningIconLarge} />
            <Typography className={classes.dialogTitle}>
              Variance/Mismatch Alert
            </Typography>
          </Grid>

          {/* Header info */}
          <div className={classes.infoHeaderRow}>
            {[
              { label: "CMO", value: row?.vendorName || "" },
              { label: "PO Number", value: row?.scheduleAgreementNumber || "" },
              { label: "Batch Number", value: row?.vendorBatchNumber || "" },
            ].map(({ label, value }) => (
              <div key={label}>
                <Typography className={classes.infoLabel}>{label}</Typography>
                <Typography className={classes.infoValue}>{value}</Typography>
              </div>
            ))}
          </div>

          {/* ── Section 1: Stat and Delivery Date Variance ── */}
          {showStatDateSection && (
            <>
              <Typography className={classes.sectionHeader}>
                Stat and Delivery Date Variance
              </Typography>
              <TableContainer
                component={Paper}
                className={classes.tableContainer}
                style={{ overflowX: "hidden" }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHeaderCell}>SAP PO Values – Statistical Date</TableCell>
                      <TableCell className={classes.tableHeaderCell}>SAP PO Values – Delivery Date</TableCell>
                      <TableCell className={classes.tableHeaderCell}>Present Date</TableCell>
                      <TableCell className={classes.tableHeaderCell}>Early Variance Limit (days)</TableCell>
                      <TableCell className={classes.tableHeaderCell}>Later Variance Limit (days)</TableCell>
                      {showUpdateStatDateCol && (
                        <TableCell className={classes.tableHeaderCell}>
                          Update SAP PO Stat Date to <span style={{ color: "red" }}>*</span>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell className={classes.tableCell}>{formatDisplayDate(row?.statisticalDate)}</TableCell>
                      <TableCell className={classes.tableCell}>{formatDisplayDate(row?.deliveryDate)}</TableCell>
                      <TableCell className={classes.tableCell}>{formatDisplayDate(presentDate.toISOString())}</TableCell>
                      <TableCell className={classes.tableCell}>{row?.earlyDateVarianceLimit ?? "-"}</TableCell>
                      <TableCell className={classes.tableCell}>{row?.laterDateVarianceLimit ?? "-"}</TableCell>
                      {showUpdateStatDateCol && (
                        <TableCell className={classes.tableCell}>
                          {isStatDateEditable ? (
                            <CustomDatePickerMui
                              name="updateStatDate"
                              value={updateStatDate}
                              onChange={(momentObj) => setUpdateStatDate(momentObj)}
                              size="small"
                              notFullWidth
                            />
                          ) : (
                            // Automatic after approval, early direction: show the
                            // Present Date (matches what's actually sent on
                            // Approve below), not the stale SAP statisticalDate.
                            // Later direction is unchanged — echoes statisticalDate.
                            <span>
                              {formatDisplayDate(
                                isEarlyDateVariance ? presentDate.toISOString() : row?.statisticalDate
                              )}
                            </span>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* ── Section 2: Consumed Batch Variance/Mismatch ── */}
          {showConsumedBatchSection && (
            <>
              <Typography className={classes.sectionHeader}>
                Consumed Batch Variance/Mismatch
              </Typography>
              {loadingAllocation ? (
                <Grid container justifyContent="center" style={{ padding: "20px" }}>
                  <CircularProgress size={28} />
                </Grid>
              ) : (
                <TableContainer
                  component={Paper}
                  className={classes.tableContainer}
                  style={{ overflowX: "hidden" }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.tableHeaderCell}>BMS Material Code / Description</TableCell>
                        <TableCell className={classes.tableHeaderCell}>BoM Quantity</TableCell>
                        <TableCell className={classes.tableHeaderCell}>UOM</TableCell>
                        <TableCell className={classes.tableHeaderCell}>Batch Numbers</TableCell>
                        <TableCell className={classes.tableHeaderCell}>Planned Allocated Qty</TableCell>
                        <TableCell className={classes.tableHeaderCell}>Actual Consumed Qty</TableCell>
                        {showConsumedQtyVarianceCol && (
                          <TableCell className={classes.tableHeaderCell}>Consumed Quantity Variance (%)</TableCell>
                        )}
                        {showBatchesMismatchCol && (
                          <TableCell className={classes.tableHeaderCell}>Batches Mismatch</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {consumedRows.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={6 + (showConsumedQtyVarianceCol ? 1 : 0) + (showBatchesMismatchCol ? 1 : 0)}
                            align="center"
                            className={classes.tableCell}
                          >
                            No consumption data available
                          </TableCell>
                        </TableRow>
                      ) : (
                        consumedRows.map((material, mIdx) =>
                          material.lots.map((lot, lIdx) => (
                            <TableRow key={`${mIdx}-${lIdx}`}>
                              {lIdx === 0 && (
                                <TableCell
                                  className={classes.tableCell}
                                  rowSpan={material.lots.length}
                                  style={{ verticalAlign: "middle" }}
                                >
                                  <div>{material.materialCode} /</div>
                                  <div>{material.materialName}</div>
                                </TableCell>
                              )}
                              {lIdx === 0 && (
                                <TableCell
                                  className={classes.tableCell}
                                  rowSpan={material.lots.length}
                                  style={{ verticalAlign: "middle" }}
                                >
                                  {toDecimal(material.bomQuantity)}
                                </TableCell>
                              )}
                              {lIdx === 0 && (
                                <TableCell
                                  className={classes.tableCell}
                                  rowSpan={material.lots.length}
                                  style={{ verticalAlign: "middle" }}
                                >
                                  {material.uom}
                                </TableCell>
                              )}
                              <TableCell className={classes.tableCell}>{lot.batchNumber}</TableCell>
                              <TableCell className={classes.tableCell}>
                                {toDecimal(lot.plannedAllocatedQty)}
                              </TableCell>
                              <TableCell className={classes.tableCell}>
                                {toDecimal(lot.actualConsumedQty)}
                              </TableCell>
                              {lIdx === 0 && showConsumedQtyVarianceCol && (
                                <TableCell
                                  className={classes.tableCell}
                                  rowSpan={material.lots.length}
                                  style={{ verticalAlign: "middle" }}
                                >
                                  {(() => {
                                    const variance = material.consumedQtyVariance;
                                    if (variance === "NA" || variance === null || variance === undefined) {
                                      return <span></span>;
                                    }
                                    if (variance === 0) {
                                      return "0";
                                    }
                                    const displayValue = variance % 1 !== 0 ? Number(variance).toFixed(3) : variance;
                                    const isOverLimit = qtyVarianceAcceptanceLimit != null
                                      ? Math.abs(Number(variance)) > qtyVarianceAcceptanceLimit
                                      : true;
                                    return (
                                      <span className={isOverLimit ? classes.warningInline : undefined}>
                                        {displayValue}
                                        {material.varianceWarning && (
                                          <WarningIcon className={classes.warningIconSmall} />
                                        )}
                                      </span>
                                    );
                                  })()}
                                </TableCell>
                              )}
                              {lIdx === 0 && showBatchesMismatchCol && (
                                <TableCell
                                  className={classes.tableCell}
                                  rowSpan={material.lots.length}
                                  style={{ verticalAlign: "middle" }}
                                >
                                  {material.batchesMismatch ? (
                                    <span className={classes.yesWarning}>
                                      YES&nbsp;
                                      <WarningIcon className={classes.warningIconSmall} />
                                    </span>
                                  ) : (
                                    "NO"
                                  )}
                                </TableCell>
                              )}
                            </TableRow>
                          ))
                        )
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* ── Section 3: Produced Qty Variance ── */}
          {showProductQtySection && (
            <>
              <Typography className={classes.sectionHeader}>Produced Qty Variance</Typography>
              <TableContainer
                component={Paper}
                className={classes.tableContainer}
                style={{ overflowX: "hidden" }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHeaderCell}>BMS Product Code / Description</TableCell>
                      <TableCell className={classes.tableHeaderCell}>UOM</TableCell>
                      <TableCell className={classes.tableHeaderCell}>PO Qty</TableCell>
                      <TableCell className={classes.tableHeaderCell}>Produced Qty</TableCell>
                      <TableCell className={classes.tableHeaderCell}>Produced Quantity Variance (%)</TableCell>
                      {showLessVarianceLimitCol && (
                        <TableCell className={classes.tableHeaderCell}>Less Variance Limit (%)</TableCell>
                      )}
                      {showMoreVarianceLimitCol && (
                        <TableCell className={classes.tableHeaderCell}>More Variance Limit (%)</TableCell>
                      )}
                      {showUpdatePOQtyCol && (
                        <TableCell className={classes.tableHeaderCell}>
                          Update SAP PO Qty to{isProductQtyEditable ? <span style={{ color: "red" }}> *</span> : ""}
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productQtyRow ? (
                      <TableRow>
                        <TableCell className={classes.tableCell}>
                          {productQtyRow.productCode} / {productQtyRow.productName}
                        </TableCell>
                        <TableCell className={classes.tableCell}>{productQtyRow.uom}</TableCell>
                        <TableCell className={classes.tableCell}>{toDecimal(productQtyRow.poQty)}</TableCell>
                        <TableCell className={classes.tableCell}>{toDecimal(productQtyRow.producedQty)}</TableCell>
                        <TableCell className={classes.tableCell}>
                          <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            {productQtyRow.variance}
                            {row?.producedQtyVariancePresent && (
                              <WarningIcon className={classes.warningIconSmall} />
                            )}
                          </span>
                        </TableCell>
                        {showLessVarianceLimitCol && (
                          <TableCell className={classes.tableCell}>
                            {row?.lessProducedQtyVarianceLimitPercentage ?? "-"}
                          </TableCell>
                        )}
                        {showMoreVarianceLimitCol && (
                          <TableCell className={classes.tableCell}>
                            {row?.moreProducedQtyVarianceLimitPercentage ?? "-"}
                          </TableCell>
                        )}
                        {showUpdatePOQtyCol && (
                          <TableCell className={classes.tableCell}>
                            {isProductQtyEditable ? (
                              <input
                                type="number"
                                value={updatePOQty}
                                onChange={(e) => setUpdatePOQty(e.target.value)}
                                style={{ width: "80px", padding: "4px", border: "1px solid #ccc", borderRadius: 4 }}
                              />
                            ) : (
                              <input
                                type="number"
                                value={row?.producedQuantity ?? ""}
                                disabled
                                style={{ width: "80px", padding: "4px", border: "1px solid #ccc", borderRadius: 4, backgroundColor: "#f0f0f0" }}
                              />
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={5 + (showLessVarianceLimitCol ? 1 : 0) + (showMoreVarianceLimitCol ? 1 : 0) + (showUpdatePOQtyCol ? 1 : 0)}
                          align="center"
                          className={classes.tableCell}
                        >
                          No data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {approveError && (
            <Typography className={classes.errorText}>{approveError}</Typography>
          )}
          </>
        </DialogContent>

        <DialogActions
          style={{
            justifyContent: "center",
            gap: "24px",
            padding: "20px 36px 24px 36px",
          }}
        >
          <Button
            variant="contained"
            className={classes.rejectButton}
            onClick={handleReject}
            disabled={approving}
          >
            Reject
          </Button>
          <Button
            variant="contained"
            className={classes.approveButton}
            onClick={handleApprove}
            disabled={approving}
            startIcon={approving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {approving ? "Approving..." : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={approveSuccess}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handleOk();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent className={classes.successDialog}>
          <CheckCircleIcon className={classes.successIcon} />
          <Typography className={classes.successTitle}>
            Variance/Mismatch Approved Successfully
          </Typography>
          <Button
            variant="contained"
            className={classes.okButton}
            onClick={handleOk}
            endIcon={<ChevronRightIcon />}
          >
            OK
          </Button>
        </DialogContent>
      </Dialog>

      {/* Review Consumption Dialog — opened after successful approval when needed */}
      {/* <ReviewConsumptionDialog
        open={reviewConsumptionOpen}
        handleClose={() => {
          setReviewConsumptionOpen(false);
          handleClose();
          if (onRefreshData) onRefreshData();
        }}
        row={row}
        plannedQtySnapshot={plannedQtySnapshot}
        originalPOQty={row?.targetQuantity}
        newPOQty={approvedPOQty}
        onRefreshData={onRefreshData}
      /> */}
    </>
  );
};

export default VarianceMismatchDialog;
